import React, {FC, useMemo, useRef} from 'react';

import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {styled, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import analyticsLineEmptyState from '../../../../assets/images/analyticsLineEmptyState.svg';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsMultiGraphMetricsEnum,
  isAnalyticsMetricKey,
} from '../../../../models/api/analyticsMetricsModel';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getAnalyticsPreviousPeriodDatetimes} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {useSelectedMetric} from '../../../helpers/hooks/useSelectedMetric';
import {maybeRenderMetricPillAndTooltip} from '../../../helpers/metricLabels';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {DataAccessor} from '../../charts/commons/accessors';
import {ChartSerie} from '../../charts/commons/types';
import {ChartLegend} from '../../charts/components/legend/chartLegend';
import {LineChart, LineChartEventData} from '../../charts/components/lineChart/lineChart';
import {StyledElementWrapper, StyledFlex} from '../../reportViewer/reportCommons';
import {ChartExportViewDialog} from '../export/chartExportViewDialog';
import {AnyGraphMetric, AnyGraphMetricsEnum} from '../helperTypes';
import {
  ReportElementMultiMetricsProps,
  ReportElementWrapper,
  ReportElementWrapperProps,
} from '../reportElement';
import {AnalyticsTooltip} from '../tooltips/analyticsTooltips';
import {ChartProps} from './chartsModel';
import {generateCsvFormattedData} from './common/generateExportData';
import {GraphMetricsSelectionDropdown} from './common/graphMetricsSelectionDropdown';
import {
  computeSeriesWithOptionalLabels,
  createMockData,
  getNumericalSelectedMetricRenderer,
  maybeAddPreviousValue,
} from './common/helpers';
import {MetricErrorState} from './common/metricErrorState';
import {PreviousToggle} from './common/previousToggle';
import {defaultEmptyStateRenderer, isEmptyMetric} from './emptyState';
import {useGraphMetricData} from './useGraphMetricData';

/*
 * Constants.
 */

const defaultHeight = 275;

/*
 * Props.
 */

export interface ReportLineChartProps<T>
  extends ReportElementWrapperProps,
    ChartProps,
    ReportElementMultiMetricsProps<AnyGraphMetricsEnum> {
  series: Array<ChartSerie<T>>;
  xAccessor: DataAccessor<T>;
  disablePreviousToggle?: boolean;
  onDataPointSelect?: (bar: T, serie: ChartSerie<T>, metric?: AnyGraphMetricsEnum) => void;
}

/*
 * Styles.
 */

const StyledDropdownSelectedLabelDiv = styled.div`
  ${flexCenter}
  gap: 6px
`;

/*
 * Component.
 */

export const ReportLineChart = <T,>(props: ReportLineChartProps<T>): ReturnType<FC> => {
  const {
    reportSectionId,
    isLoading,
    renderEmptyState = defaultEmptyStateRenderer,
    availableMetrics,
    width,
    height,
    series,
    xAccessor,
    disablePreviousToggle,
    onDataPointSelect,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(containerRef);

  const theme = useUserTheme();

  const trackAnalytics = useAnalyticsTracking();
  const {updateSelectedMetrics, maybeFetchHiddenMetrics} = useAnalyticsUpdateSelectedMetrics();

  const [selectedMetric, labels] = useSelectedMetric(reportSectionId, availableMetrics);
  const {tooltip, label, series: seriesLabels, isBeta, isNew} = labels;

  const title = props.title ?? label;

  const [previousPeriodStart, previousPeriodEnd] = useAppSelector(getAnalyticsPreviousPeriodDatetimes);
  const [shouldDisplayPrevious, toggleShouldDisplayPrevious] = useToggle(false);
  const [shouldShowExportDialog, toggleExportDialog] = useToggle();

  const trackAndSetSelectedMetric = (metric: AnyGraphMetricsEnum) => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_SELECT, {
      metric_type: metric,
      report_section: reportSectionId,
    });

    if (isAnalyticsMetricKey(metric)) {
      maybeFetchHiddenMetrics(metric);
    } // Will fetch the metrics if not already present.

    updateSelectedMetrics([metric], reportSectionId);
  };

  const onShouldDisplayPreviousClick = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_COMPARE_PREVIOUS_CLICK, {
      click_type: shouldDisplayPrevious ? 'deselect' : 'select',
    });

    toggleShouldDisplayPrevious();
  };

  const handleDataPointSelect = (eventData?: LineChartEventData<T>) => {
    if (!eventData || !onDataPointSelect) {
      return;
    }

    const {data, serie} = eventData;

    onDataPointSelect(data, serie, selectedMetric);
  };

  const seriesWithTooltip =
    // Wrap the labels with tooltips to display them in the legend
    series.map((serie, index) => ({
      ...serie,
      label: <AnalyticsTooltip tooltip={seriesLabels?.[index]?.tooltip}>{serie.label}</AnalyticsTooltip>,
    }));

  const mockedData = useMemo(() => createMockData(series), [series]);
  const [graphMetricData, metricError] = useGraphMetricData<T, AnyGraphMetric>(
    selectedMetric,
    (metricData?: AnyGraphMetric) => isLoading || !metricData,
    mockedData,
  );

  // TODO: Simplify type inference of the output of useGraphMetricData to remove the need for casting.
  const graphMetricValues = (graphMetricData?.values as Array<T>) ?? [];

  const renderSelectedMetric = getNumericalSelectedMetricRenderer(graphMetricData);

  const yAxisTickFormatter = (val: number) => {
    if (!renderSelectedMetric) {
      return val.toString();
    }

    return (renderSelectedMetric({value: val}) || '').toString();
  };

  const isEmpty = !isLoading && isEmptyMetric(graphMetricData);

  const chartSeries = useMemo(() => {
    const chartSeriesWithLabel = computeSeriesWithOptionalLabels(
      selectedMetric,
      series,
      label,
      availableMetrics.length > 1,
    );
    const chartSeriesWithPreviousAndLabel = maybeAddPreviousValue(
      graphMetricData,
      Boolean(shouldDisplayPrevious),
      chartSeriesWithLabel,
    );
    return chartSeriesWithPreviousAndLabel;
  }, [availableMetrics.length, graphMetricData, label, selectedMetric, series, shouldDisplayPrevious]);

  const renderChart = () => {
    if (metricError) {
      return (
        <MetricErrorState
          error={metricError}
          width={width ?? containerSizeInfo?.width}
          height={height ?? defaultHeight}
        />
      );
    }

    if (isEmpty) {
      return renderEmptyState({
        width: width ?? containerSizeInfo?.width,
        height: height ?? defaultHeight,
        image: analyticsLineEmptyState,
      });
    }

    if (containerSizeInfo) {
      return (
        <LineChart
          width={width ?? containerSizeInfo.width}
          height={height ?? defaultHeight}
          isLoading={graphMetricData?.isMocked}
          xAccessor={xAccessor}
          data={graphMetricValues}
          yAxisTickFormatter={yAxisTickFormatter}
          series={chartSeries}
          onClick={handleDataPointSelect}
        />
      );
    }

    return null;
  };

  return (
    <ReportElementWrapper
      reportSectionId={reportSectionId}
      title={title}
      infoTooltip={tooltip}
      isExportable
      toggleExportDialog={toggleExportDialog}
    >
      <StyledFlex>
        {availableMetrics.length > 1 && (
          <GraphMetricsSelectionDropdown
            availableMetrics={availableMetrics}
            selectedMetric={selectedMetric}
            handleSelectedMetric={trackAndSetSelectedMetric}
            chartSeries={series}
            selectedLabel={
              <StyledDropdownSelectedLabelDiv>
                {label} {maybeRenderMetricPillAndTooltip(theme, isBeta, isNew)}
              </StyledDropdownSelectedLabelDiv>
            }
          />
        )}
        {!disablePreviousToggle && (
          <PreviousToggle
            shouldDisplayPrevious={shouldDisplayPrevious}
            toggleShouldDisplayPrevious={onShouldDisplayPreviousClick}
            chartSeries={chartSeries}
            previousPeriod={{start: previousPeriodStart, end: previousPeriodEnd}}
          />
        )}
      </StyledFlex>
      {availableMetrics.length === 1 && <ChartLegend series={seriesWithTooltip} />}
      <StyledElementWrapper ref={containerRef}>
        {renderChart()}
        {/* Export Dialog */}
        {shouldShowExportDialog && (
          <ChartExportViewDialog
            onRequestClose={toggleExportDialog}
            generateFormattedData={generateCsvFormattedData(
              xAccessor,
              chartSeries,
              graphMetricValues,
              selectedMetric as AnalyticsMultiGraphMetricsEnum,
            )}
            title={reportSectionId}
          />
        )}
      </StyledElementWrapper>
    </ReportElementWrapper>
  );
};
